import React from 'react';
import { BarChart, Bar, Cell, ResponsiveContainer, PieChart, Pie } from 'recharts';

const NPSScoreComponent = ({ data }) => {
  // Your data structure has npsScore directly and sentimentDistribution
  const score = data.npsScore;
  const totalReviews = Object.values(data.sentimentDistribution).reduce((sum, count) => sum + count, 0);
  
  // Calculate percentages for each sentiment type
  const promoters = data.sentimentDistribution.Promoter || 0;
  const passives = data.sentimentDistribution.Passive || 0;
  const detractors = data.sentimentDistribution.Detractor || 0;
  
  const distribution = [
    { 
      name: 'Promoters', 
      value: promoters, 
      percentage: Math.round((promoters / totalReviews) * 100),
      color: '#22c55e' // Green for Promoters
    },
    { 
      name: 'Passives', 
      value: passives, 
      percentage: Math.round((passives / totalReviews) * 100),
      color: '#94a3b8' // Gray for Passives
    },
    { 
      name: 'Detractors', 
      value: detractors, 
      percentage: Math.round((detractors / totalReviews) * 100),
      color: '#ef4444' // Red for Detractors
    }
  ];
  
  // Calculate color based on NPS score
  const getScoreColor = (score) => {
    if (score >= 70) return '#22c55e'; // Green for good
    if (score >= 50) return '#94a3b8'; // Gray for average
    return '#ef4444'; // Red for poor
  };

  // Create data for semi-circular progress chart
  // Normalize score from -100 to 100 range to 0 to 1 range
  const normalizedScore = (score + 100) / 200;
  
  // Create arc segments for different score zones
  const arcData = [
    { name: 'Poor', value: 0.33, color: '#ef4444' }, // Red for poor
    { name: 'Average', value: 0.33, color: '#94a3b8' }, // Gray for average
    { name: 'Good', value: 0.34, color: '#22c55e' } // Green for good
  ];
  
  // Create progress indicator
  const progressData = [
    { name: 'Progress', value: normalizedScore, color: getScoreColor(score) },
    { name: 'Remaining', value: 1 - normalizedScore, color: '#e5e7eb' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex  justify-between mb-4 flex-col">
        <h2 className="text-xl font-bold text-gray-800">NPS Score</h2>
        <p className="text-sm text-gray-500">Based on {totalReviews} NPS responses and customer feedback</p>
      </div>
      
      {/* NPS Arc Chart */}
      <div className="h-48 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Background segments */}
            <Pie
              data={arcData}
              cx="50%"
              cy="80%"
              startAngle={180}
              endAngle={0}
              innerRadius="60%"
              outerRadius="80%"
              dataKey="value"
              stroke="none"
            >
              {arcData.map((entry, index) => (
                <Cell key={`cell-bg-${index}`} fill={entry.color} opacity={0.2} />
              ))}
            </Pie>
            
            {/* Progress arc */}
            <Pie
              data={progressData}
              cx="50%"
              cy="80%"
              startAngle={180}
              endAngle={0}
              innerRadius="85%"
              outerRadius="95%"
              dataKey="value"
              stroke="none"
            >
              {progressData.map((entry, index) => (
                <Cell key={`cell-progress-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        
        {/* Score display at the diameter level */}
        <div className="absolute" style={{ top: '80%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <div className="text-5xl font-bold" style={{ color: getScoreColor(score) }}>
            {score}
          </div>
        </div>
      </div>
      
      <div className="flex items-end gap-4 mt-6 border-t pt-4 border-gray-100">
        <div className="flex-1">
          <div className="flex justify-between text-xs mt-1">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
              <span>Promoters ({distribution[0].percentage}%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-gray-400 mr-1"></div>
              <span>Passive ({distribution[1].percentage}%)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-1"></div>
              <span>Detractors ({distribution[2].percentage}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPSScoreComponent;